import { useCallback, useEffect, useRef, useState } from "react";

export interface InfoCriterion {
  label: string;
  value: string;
  /** Marks a criterion the map is NOT currently colouring against, so the
   *  reference text and the map can never silently contradict each other. */
  applied?: boolean;
}

export interface InfoSection {
  heading?: string;
  body?: string[];
  criteria?: InfoCriterion[];
  note?: string;
}

export interface InfoContent {
  title: string;
  /** Opening paragraphs, before the first heading. */
  lede?: string[];
  sections: InfoSection[];
  footer?: string[];
}

/**
 * The "About this data" dialog, shared by all three programs so the
 * presentation is identical and only the copy differs.
 *
 * Built on the native <dialog> element rather than a hand-rolled overlay: it
 * brings the focus trap, Escape-to-close, inertness of the page behind and
 * the top-layer stacking with it, none of which is worth reimplementing and
 * all of which is easy to get subtly wrong.
 */
export function InfoDialog({
  content, open, onClose,
}: { content: InfoContent; open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Escape and the close button both route through the element's own event.
  const handleClose = useCallback(() => onClose(), [onClose]);

  return (
    <dialog
      className="infodlg"
      ref={ref}
      onClose={handleClose}
      onCancel={handleClose}
      aria-labelledby="infodlg-title"
      // Clicking the backdrop: the dialog element itself is the backdrop's
      // hit target, so a click landing on it rather than on the inner panel
      // means outside.
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
    >
      <div className="infodlg-panel">
        <header className="infodlg-head">
          <h2 id="infodlg-title">{content.title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" fill="none" />
            </svg>
          </button>
        </header>

        <div className="infodlg-body">
          {content.lede?.map((p, i) => <p className="infodlg-lede" key={i}>{p}</p>)}

          {content.sections.map((s, i) => (
            <section className="infodlg-section" key={i}>
              {s.heading && <h3>{s.heading}</h3>}
              {s.body?.map((p, j) => <p key={j}>{p}</p>)}
              {s.criteria && s.criteria.length > 0 && (
                <dl className="infodlg-criteria">
                  {s.criteria.map((c, j) => (
                    <div key={j} className={c.applied === false ? "is-unapplied" : undefined}>
                      <dt>{c.label}</dt>
                      <dd>{c.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {s.note && <p className="infodlg-note">{s.note}</p>}
            </section>
          ))}

          {content.footer?.map((p, i) => <p className="infodlg-foot" key={i}>{p}</p>)}
        </div>
      </div>
    </dialog>
  );
}

/** The trigger, plus the dialog it owns. Drop one of these in the header. */
export default function InfoButton({ content }: { content: InfoContent }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="infobtn"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        title={content.title}
      >
        <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
          <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="10" cy="5.9" r="1.05" fill="currentColor" />
          <path d="M10 8.9v5.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
        <span className="infobtn-label">About</span>
      </button>
      <InfoDialog content={content} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
